'use client';

import React, { useCallback, useState, useEffect, useRef } from 'react';
import classNames from 'classnames';

import { searchClient } from 'components/essentials/AlgoliaSearchClient';
import { InstantSearch, Configure, Index } from 'react-instantsearch';

import { ALGOLIA_INDEX_NAME } from 'commons/constants';
import useOpenSearch from 'global-state/isSearchOpen/useOpenSearch';
import useIsSearchOpen from 'global-state/isSearchOpen/useIsSearchOpen';
import useCloseSearch from 'global-state/isSearchOpen/useCloseSearch';
import useAdvancedSearchURL from '../advanced-search-next/hooks/useAdvancedSearchURL';
import useIsAdvancedSearch from '../hooks/useIsAdvancedSearch';
import JBSearchBoxNext from './components/JBSearchBoxNext';
import SearchResultsWrapperNext from './components/SearchResultsWrapperNext';
import SearchModalProductGridNext from './components/SearchModalProductGridNext';
import SeeFullResultsButtonNext from './components/SeeFullResultsButtonNext';
import CategoryHitsNext from './components/CategoryHitsNext';
import ResultsByCategoryNext from './components/ResultsByCategoryNext';
import IndexHitsCount from './components/ResultsByCategoryNext/components/IndexHitsCount';
import ClearModalSearchQueryWhenClosed from './components/ClearModalSearchQueryWhenClosed';

/**
 * Consumer Search Bar - Next version (for new Consumer app)
 * Uses React InstantSearch v7
 */
const ConsumerSearchBarNext = () => {
  const [isSearchBarActivated, setisSearchBarActivated] = useState(false);
  const prevPathnameRef = useRef(null);

  const closeSearch = useCloseSearch();
  const openSearch = useOpenSearch();
  const isSearchOpen = useIsSearchOpen();
  const isAdvancedSearch = useIsAdvancedSearch();
  const { pathname } = useAdvancedSearchURL();

  useEffect(() => {
    if (
      prevPathnameRef.current !== null &&
      prevPathnameRef.current !== pathname
    ) {
      closeSearch();
      setisSearchBarActivated(false);
    }
    prevPathnameRef.current = pathname;
  }, [pathname, closeSearch]);

  const handleOverlayKeyDown = useCallback(
    e => {
      if (e.key === 'Enter' || e.key === ' ') {
        closeSearch();
      }
    },
    [closeSearch]
  );

  const handleSearchBarFocus = useCallback(() => {
    setisSearchBarActivated(true);
    openSearch();
  }, [setisSearchBarActivated, openSearch]);

  return (
    <div
      className={classNames(
        'bg-white max-[1023px]:relative max-[1023px]:z-10',
        {
          hidden: isAdvancedSearch,
        }
      )}
    >
      {/* Search Modal */}
      <div className="bg-white relative max-[1023px]:z-10 lg:!z-[1002]">
        <InstantSearch
          searchClient={searchClient}
          indexName={ALGOLIA_INDEX_NAME.PRODUCTS}
        >
          <Configure
            hitsPerPage={3}
            facets={['*']}
            numericFilters={[
              'item_availability<2',
              'visibility>0',
              'visibility<4',
            ]}
            attributesToRetrieve={['_highlightResult', 'product_id', 'name']}
          />

          <ClearModalSearchQueryWhenClosed />

          <JBSearchBoxNext
            autoFocus
            isActive
            setInactive={() => {}}
            toggleSearchOpen={closeSearch}
            variant="ConsumerInstantSearch"
            handleSearchBarFocus={handleSearchBarFocus}
          />
          {isSearchOpen && (
            <SearchResultsWrapperNext>
              <div
                className="bg-white absolute w-full"
                data-xc="search-dropdown-preview"
              >
                <div className="flex w-full max-w-none mx-auto px-2 gap-6">
                  <div className="flex-1">
                    <div
                      className="bg-gray-50 p-6 rounded-lg"
                      data-xc="search-dropdown-products"
                    >
                      <SearchModalProductGridNext />
                      <div className="mt-6 flex justify-center">
                        <SeeFullResultsButtonNext />
                      </div>
                    </div>
                  </div>

                  <div className="w-64">
                    <div
                      className="bg-gray-50 p-0 rounded-lg h-full"
                      data-xc="search-dropdown-categories"
                    >
                      <Index indexName="categories">
                        <Configure
                          hitsPerPage={5}
                          numericFilters={['fk_store_id=1']}
                          facetFilters={['is_searchable:1']}
                          attributesToRetrieve={[
                            'objectID',
                            'category_name',
                            'category_sef_url',
                            'url',
                          ]}
                        />
                        <CategoryHitsNext toggleSearchOpen={closeSearch} />
                      </Index>
                    </div>
                  </div>

                  <div className="w-64">
                    <div
                      className="bg-gray-50 p-6 rounded-lg h-full"
                      data-xc="search-dropdown-results-by"
                    >
                      <ResultsByCategoryNext toggleSearchOpen={closeSearch} />
                      <Index indexName="reviews">
                        <Configure
                          facets={['*']}
                          numericFilters=""
                          facetFilters=""
                        />
                        <IndexHitsCount
                          indexName="reviews"
                          toggleSearchOpen={closeSearch}
                        />
                      </Index>
                      <Index indexName="customer_photos">
                        <Configure facets={['*']} numericFilters="" />
                        <IndexHitsCount
                          indexName="customer_photos"
                          toggleSearchOpen={closeSearch}
                        />
                      </Index>
                      <Index indexName={ALGOLIA_INDEX_NAME.STATIC_PAGES}>
                        <Configure
                          numericFilters={['is_active=1', 'show_in_search=1']}
                        />
                        <IndexHitsCount
                          indexName={ALGOLIA_INDEX_NAME.STATIC_PAGES}
                          toggleSearchOpen={closeSearch}
                        />
                      </Index>
                    </div>
                  </div>
                </div>
              </div>
            </SearchResultsWrapperNext>
          )}
        </InstantSearch>
      </div>
      {/* Overlay Background */}
      <div
        className={classNames(
          'fixed inset-0 bg-blackOverlay z-[1001] transition-opacity duration-300 ease-out',
          {
            hidden: !isSearchOpen || !isSearchBarActivated,
            block: isSearchOpen && isSearchBarActivated,
          }
        )}
        onClick={closeSearch}
        role="button"
        tabIndex={0}
        onKeyDown={handleOverlayKeyDown}
        aria-label="Close search modal"
      />
    </div>
  );
};

export default ConsumerSearchBarNext;
